import type { ReactNode } from 'react';

/**
 * OWNER-OPERATING-SYSTEM-V1 connected KPI band (growth brief s.25.4; the
 * polish layer's card taxonomy s.25.2). ONE raised surface per band; members
 * render as FLAT cells divided by internal hairlines -- never floating white
 * boxes, never a raised card inside a raised card (elevation scale s.25.1).
 *
 * DRILL AFFORDANCE DEDUP (s.25.10): the band header states the period +
 * comparison basis ONCE and carries the band's SINGLE drill affordance plus
 * at most ONE history disclosure (native details/summary -- keyboard
 * reachable, no JS required). Cells are silently clickable when onClick is
 * given; per-cell drill labels are forbidden.
 *
 * PRESENTATION-ONLY: values arrive fully formatted/resolved from the caller;
 * this component fetches nothing and derives nothing. A cell with no honest
 * value renders the caller's seam node (SEAM law) -- never a fake numeral.
 */

export interface KpiBandCellDef {
  readonly key: string;
  readonly label: string;
  /** The hero value, already formatted (DENSITY rule 3: value is largest). */
  readonly value: ReactNode;
  /** Signed printed delta (never color-alone); direction picks the tone. */
  readonly delta?: {
    readonly text: string;
    readonly direction: 'up' | 'down' | 'flat';
  };
  readonly meta?: ReactNode;
  /** CSS-only sparkline (04-DATA-VIZ s.2): captioned window, static bars. */
  readonly spark?: {
    readonly values: readonly number[];
    readonly caption: string;
  };
  readonly onClick?: () => void;
  readonly testId?: string;
}

export interface KpiBandProps {
  readonly title: string;
  /** Packs the cells on a narrower track so a SIX-fact band still holds on
   *  one row at 1280 (OWNER-DAILY-EOD-REPORT-V2 fit proof). Density only --
   *  the type scale, weights and spacing are untouched. */
  readonly compact?: boolean;
  /** Comparison basis, printed once in the band header (s.25.10). */
  readonly basis?: string;
  /** ONE optional history disclosure (the like-date panel). */
  readonly history?: { readonly summary: string; readonly content: ReactNode };
  /** The band's ONE drill affordance (e.g. "in Reports"). */
  readonly drill?: ReactNode;
  readonly cells: ReadonlyArray<KpiBandCellDef>;
  readonly children?: ReactNode;
  readonly testId?: string;
}

function sparkBars(spark: NonNullable<KpiBandCellDef['spark']>): ReactNode {
  const max = spark.values.reduce((m, v) => (v > m ? v : m), 0);
  return (
    <>
      <span className="ac-spark" aria-hidden="true">
        {spark.values.map((v, i) => (
          <i
            key={i}
            className={i === spark.values.length - 1 ? 'ac-spark__bar ac-spark__bar--last' : 'ac-spark__bar'}
            style={{ height: `${max > 0 ? Math.max(6, Math.round((v / max) * 100)) : 6}%` }}
          />
        ))}
      </span>
      <span className="ac-spark__cap">{spark.caption}</span>
    </>
  );
}

function CellBody({ cell }: { readonly cell: KpiBandCellDef }): JSX.Element {
  return (
    <>
      <span className="ac-kcell__label">{cell.label}</span>
      <span className="ac-kcell__value">{cell.value}</span>
      {cell.delta ? (
        <span className={`ac-kcell__delta ac-kcell__delta--${cell.delta.direction}`}>
          {cell.delta.text}
        </span>
      ) : null}
      {cell.spark ? sparkBars(cell.spark) : null}
      {cell.meta !== undefined && cell.meta !== null ? (
        <span className="ac-kcell__meta">{cell.meta}</span>
      ) : null}
    </>
  );
}

export default function KpiBand({
  title,
  basis,
  history,
  drill,
  cells,
  compact = false,
  children,
  testId,
}: KpiBandProps): JSX.Element {
  return (
    <section
      className={compact ? 'ac-kband ac-kband--compact' : 'ac-kband'}
      data-testid={testId}
    >
      <div className="ac-kband__head">
        <span className="ac-kband__title">{title}</span>
        {basis ? <span className="ac-kband__basis">{basis}</span> : null}
        {history ? (
          <details className="ac-kband__hist">
            <summary>{history.summary}</summary>
            <div className="ac-kband__histpanel">{history.content}</div>
          </details>
        ) : null}
        {drill ? <span className="ac-kband__drill">{drill}</span> : null}
      </div>
      <div className="ac-kband__cells">
        {cells.map((cell) =>
          cell.onClick ? (
            <button
              key={cell.key}
              type="button"
              className="ac-kcell ac-kcell--link"
              onClick={cell.onClick}
              data-testid={cell.testId}
            >
              <CellBody cell={cell} />
            </button>
          ) : (
            <div key={cell.key} className="ac-kcell" data-testid={cell.testId}>
              <CellBody cell={cell} />
            </div>
          ),
        )}
      </div>
      {children}
    </section>
  );
}
